import React from "react"

interface PaginationProps {
  page: number
  setPage: (page: number) => void
  totalItems: number
  itemsPerPage: number
  setItemsPerPage: (itemsPerPage: number) => void
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  setPage,
  totalItems,
  itemsPerPage,
  setItemsPerPage,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  const startPage = Math.max(1, page - 2)
  const endPage = Math.min(totalPages, page + 2)

  const pages: number[] = []
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  const from = (page - 1) * itemsPerPage + 1
  const to = Math.min(page * itemsPerPage, totalItems)

  return (
    <div className="flex items-center justify-between p-4 bg-white w-full">
      <div className="flex items-center space-x-2 text-sm text-gray-700">
        <span>Showing</span>
        <span className="font-bold">{from}</span>
        <span>to</span>
        <span className="font-bold">{to}</span>
        <span>of</span>
        <span className="font-bold">{totalItems}</span>
        <span>entries</span>

        <select
          value={itemsPerPage}
          onChange={(e) => setItemsPerPage(Number(e.target.value))}
          className="ml-2 h-7 px-2 border border-gray-300 rounded text-sm text-gray-700"
        >
          {[10, 20, 30, 50, 100].map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </div>

      <nav className="flex items-center space-x-1">
        <button
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          className="px-3 py-1 rounded-l-lg border border-gray-300 text-gray-500 bg-gray-100 disabled:opacity-50 rounded hover:bg-blue-600 hover:text-white"
        >
          ‹
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => setPage(1)}
              className="px-3 py-1 border border-gray-300 bg-gray-100 rounded hover:bg-blue-600 hover:text-white"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
          </>
        )}

        {pages.map((p) => (
          <button
            key={p}
            onClick={() => setPage(p)}
            className={`px-3 py-1 border border-gray-300 rounded hover:bg-blue-600 hover:text-white ${
              p === page ? "bg-blue-600 font-semibold text-white" : "bg-gray-100"
            }`}
          >
            {p}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2 text-gray-500">...</span>}
            <button
              onClick={() => setPage(totalPages)}
              className="px-3 py-1 border border-gray-300 bg-gray-100 rounded hover:bg-blue-600 hover:text-white"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
          className="px-3 py-1 rounded-r-lg border border-gray-300 text-gray-500 bg-gray-100 disabled:opacity-50 rounded hover:bg-blue-600 hover:text-white"
        >
          ›
        </button>
      </nav>
    </div>
  )
}

export default Pagination
